import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener, Theme, useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import Button, { ButtonProps } from 'components/UI/V2/Button/Button';
import { useDropdownStyles } from 'components/UI/V2/Dropdown/Dropdown.styles';
import { MobileDrawerItem } from 'components/UI/V2/MobileDrawer/Item/MobileDrawerItem';
import { MobileDrawer } from 'components/UI/V2/MobileDrawer/MobileDrawer';
import React from 'react';

interface DropdownProps<T> {
    isOpen?: boolean;
    children?: React.ReactNode;
    label: string | React.ReactNode;
    options?: Array<{
        icon?: IconProp;
        label: string | React.ReactNode;
        value: T;
    }>;
    className?: string;
    buttonProps?: Omit<ButtonProps, 'label'>;
    onSelect?: (value: T) => void;
    onOpenChange?: (isOpen: boolean) => void;
}

export const Dropdown = <T,>(props: DropdownProps<T>) => {
    const classes = useDropdownStyles();

    const isControlled = props.isOpen !== undefined;

    const ref = React.useRef<HTMLDivElement>(null);
    const desktopDropdownRef = React.useRef<HTMLDivElement>(null);

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const [isOpen, setIsOpen] = React.useState(props.isOpen ?? false);
    const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0 });

    const toggleDropdownHandler = () => {
        if (isControlled) {
            props.onOpenChange?.(!props.isOpen);
        } else {
            setIsOpen(!isOpen);
        }
    };

    const selectOptionHandler = (value: T) => {
        props.onSelect?.(value);

        if (isControlled) {
            props.onOpenChange?.(false);
        } else {
            setIsOpen(false);
        }
    };

    React.useEffect(() => {
        if (!ref.current || !desktopDropdownRef.current) return;

        const rect = ref.current.getBoundingClientRect();
        const dropdownHeight = desktopDropdownRef.current.clientHeight;
        const dropdownWidth = desktopDropdownRef.current.clientWidth;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        const margin = 16;

        // Vertical positioning
        const wouldCollideWithBottom = rect.bottom + margin + dropdownHeight > windowHeight;
        const wouldCollideWithTop = rect.top - dropdownHeight - margin < 0;

        let top: number;
        if (wouldCollideWithBottom && !wouldCollideWithTop) {
            // Position above if there's space
            top = rect.top - dropdownHeight - margin;
        } else if (wouldCollideWithBottom && wouldCollideWithTop) {
            // If it doesn't fit above or below, position to fit within viewport
            const availableSpaceBelow = windowHeight - rect.bottom - margin;
            const availableSpaceAbove = rect.top - margin;

            if (availableSpaceBelow >= availableSpaceAbove) {
                top = rect.bottom + margin;
            } else {
                top = margin;
            }
        } else {
            // Default position below
            top = rect.bottom + margin;
        }

        // Horizontal positioning
        let left = rect.left;

        // Check if dropdown would go outside right edge
        if (left + dropdownWidth > windowWidth - margin) {
            left = windowWidth - dropdownWidth - margin;
        }

        // Check if dropdown would go outside left edge
        if (left < margin) {
            left = margin;
        }

        setDropdownPosition({ top, left });
    }, [isOpen, props.isOpen]);

    return (
        <div
            ref={ref}
            className={clsx(classes.root, props.className)}
        >
            <Button
                variant="green"
                label={props.label}
                rightIcon={faAngleDown}
                {...props.buttonProps}
                onClick={toggleDropdownHandler}
            />

            {(isOpen || (isControlled && props.isOpen)) &&
                (isMobile ? (
                    <MobileDrawer onClose={toggleDropdownHandler}>
                        {props.children}

                        {props.options?.map((option, index) => (
                            <MobileDrawerItem
                                key={index}
                                icon={option.icon}
                                label={option.label}
                                onClick={() => selectOptionHandler(option.value)}
                            />
                        ))}
                    </MobileDrawer>
                ) : (
                    <ClickAwayListener onClickAway={toggleDropdownHandler}>
                        <div
                            ref={desktopDropdownRef}
                            className={classes.desktopDropdown}
                            style={{
                                top: dropdownPosition.top,
                                left: dropdownPosition.left
                            }}
                        >
                            {props.children}

                            {props.options?.map((option, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => selectOptionHandler(option.value)}
                                >
                                    {option.icon && <FontAwesomeIcon icon={option.icon} />}

                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </ClickAwayListener>
                ))}
        </div>
    );
};
